/**
 * Module 00 — Global Shell · Session bootstrap + RBAC capabilities (FE-00-07/08)
 *
 * `GET /session/bootstrap` [identity] — one call after login that hydrates
 * user, org, scope (dept/project + available lists), credits and the capability
 * set. `GET /rbac/capabilities` [identity] — capability strings for
 * hide-on-no-access. Both go through the identity client (auth interceptor +
 * `/identity-api` proxy). Optional scope params re-resolve against a different
 * org/department/project.
 */

import { identityClient } from '@/lib/api/identity';

interface ScopeParams {
  orgId?: string;
  deptId?: string;
  projectId?: string;
}

export type Role =
  | 'super_admin'
  | 'org_admin'
  | 'compliance_officer'
  | 'dept_admin'
  | 'project_admin'
  | 'power_user'
  | 'member';

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  initials: string;
  role: Role;
}

export interface OrgRef {
  id: string;
  name: string;
  plan: string;
  /** True when this user CREATED the org; false when they joined an existing
   *  one (via an invite). Drives the onboarding gate: only a founder runs
   *  first-run onboarding — an invitee joins a workspace that already exists,
   *  where creating projects is an admin action inside the app. */
  isFounder?: boolean;
}

export interface ScopeRef {
  id: string;
  name: string;
}

export interface Credits {
  used: number;
  total: number;
}

export interface SessionBootstrap {
  user: SessionUser;
  org: OrgRef;
  scope: {
    department: ScopeRef;
    project: ScopeRef;
    availableDepartments: ScopeRef[];
    availableProjects: ScopeRef[];
    /** Every org the user belongs to — more than one once they accept an
     *  invite to a second org. Absent on older backends. */
    availableOrgs?: OrgRef[];
  };
  credits: Credits;
  capabilities: string[];
}

export interface RbacCapabilities {
  role: Role;
  scope: { orgId: string; deptId: string; projectId: string };
  capabilities: string[];
}

export const sessionService = {
  async bootstrap(params: ScopeParams = {}): Promise<SessionBootstrap> {
    const res = await identityClient.get<SessionBootstrap>('/session/bootstrap', { params });
    return res.data;
  },
  async capabilities(params: ScopeParams = {}): Promise<RbacCapabilities> {
    const res = await identityClient.get<RbacCapabilities>('/rbac/capabilities', { params });
    return res.data;
  },
  /** Persist which org the shell is scoped to. The server re-checks membership. */
  async setActiveOrg(orgId: string): Promise<{ orgId: string }> {
    const res = await identityClient.post<{ orgId: string }>('/session/active-org', { orgId });
    return res.data;
  },
};
